import { useEffect, useMemo, useState } from 'react';
import type { ImpersonationSession } from './useImpersonation';
import './ImpersonationBanner.css';

interface Props {
  session: ImpersonationSession;
  onEnd: () => void;
}

/** 残り「分」を返す。期限切れ済なら 0、無期限相当 (例: 9999 年) なら巨大値。 */
function computeRemainingMinutes(expiresAtIso: string): number {
  const now = Date.now();
  const expiresAt = Date.parse(expiresAtIso);
  if (!Number.isFinite(expiresAt)) return Number.POSITIVE_INFINITY;
  return Math.max(0, Math.floor((expiresAt - now) / 60_000));
}

function formatRemaining(min: number): string {
  if (!Number.isFinite(min)) return '';
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
}

// 残り何分以下で警告バーを表示するか。
const WARNING_THRESHOLD_MIN = 60;

export function ImpersonationBanner({ session, onEnd }: Props) {
  const [remainingMin, setRemainingMin] = useState<number>(() =>
    computeRemainingMinutes(session.expires_at),
  );

  useEffect(() => {
    const tick = () => setRemainingMin(computeRemainingMinutes(session.expires_at));
    tick();
    const id = window.setInterval(tick, 30_000);
    return () => window.clearInterval(id);
  }, [session.expires_at]);

  const targetLabel = useMemo(() => {
    const org = session.target_organization;
    return (
      org?.display_name ||
      org?.name ||
      session.target_organization_id.slice(0, 8)
    );
  }, [session.target_organization, session.target_organization_id]);

  // 残り 60 分未満になったときだけ表示。無期限 (HypeApp 等) は閾値を超えるので表示されない。
  if (remainingMin >= WARNING_THRESHOLD_MIN) return null;

  return (
    <div
      className="impersonation-banner"
      role="status"
      aria-live="polite"
    >
      <div className="impersonation-banner-inner">
        <span className="impersonation-banner-text">
          <strong>{targetLabel}</strong> の代行中
        </span>
        <span className="impersonation-banner-remaining" title="セッション残り時間 (時:分)">
          残り {formatRemaining(remainingMin)}
        </span>
        <button
          type="button"
          className="btn-modal-secondary impersonation-banner-end"
          onClick={onEnd}
        >
          代行を終了
        </button>
      </div>
    </div>
  );
}
